import React, { useEffect, useState } from 'react';
import PageLayout from 'components/layouts/PageLayout';
import { useStoreWrapper } from 'stores/StoreHelper';
import LocaleLists from 'components/languageSettings/LocaleLists';
import Box from 'components/layouts/Box';
import NewButton from 'components/input/NewButton';
import { Row } from 'saladsoft-primitive';
import ServiceHelper from 'stores/ServiceHelper';
import { AdminSystemLocaleDto } from 'services/data-contracts';
import styled from 'styled-components';
import { InnerGroup } from 'components/layouts/CommonForm';
import { InnerSection } from 'components/layouts/CommonForm';
import Text from 'components/commons/Text';
import { toast } from 'react-toastify';
import IconButton from 'controls/IconButton';
import { useLocale } from 'providers/LocalProvider';
import { useStateContext } from 'providers/usePageState';

const LanguageSettingsPage = () => {
  const { uiStore } = useStoreWrapper();
  const { initLocales } = useLocale();
  const { broadcast } = useStateContext();
  const [languages, setLanguages] = useState<Array<AdminSystemLocaleDto>>([]);

  const handleLocaleChange = () => {
    uiStore?.drawer.show({
      title: '언어설정',
      children: (
        <div style={{ padding: '60px 0px' }}>
          <LocaleLists
            languages={languages}
            onChange={(selectedLanguages) => {
              handleLocaleChanged(selectedLanguages);
            }}
          />
        </div>
      ),
    });
  };

  const handleLocaleChanged = (selectedLanguages: AdminSystemLocaleDto[]) => {
    // 모든 언어에 대해 isActive 업데이트 및 API 호출
    const promises = selectedLanguages.map((lang) => {
      return ServiceHelper.adminLocaleService?.updateLocaleActivation(lang.id as number, lang);
    });

    // 모든 API 호출이 완료된 후 언어 목록 다시 불러오기
    Promise.all(promises).then(() => {
      getLanguage();
      toast.success('언어 설정이 저장되었습니다.');
      initLocales();
      broadcast({ id: 'reload', param: 'language' });
    });
  };

  const getLanguage = () => {
    ServiceHelper.adminLocaleService?.getLocales1().then((res) => {
      setLanguages(
        res.data.sort((a, b) => {
          if (a.isDefault && !b.isDefault) return -1;
          if (!a.isDefault && b.isDefault) return 1;
          return (a.id ?? 0) - (b.id ?? 0);
        }),
      );
    });
  };

  // 언어 삭제(비활성화)
  const handleDeleteLanguage = (language: AdminSystemLocaleDto) => {
    uiStore?.confirm.show({
      title: '언어삭제',
      message: '해당 언어를 삭제하시겠습니까?\n해당 언어로 작성된 컨텐츠는 모두 삭제됩니다.',
      onConfirmed: () => {
        ServiceHelper?.adminLocaleService
          ?.updateLocaleActivation(language.id as number, { isActive: false })
          .then((res) => {
            if (res.status < 300) {
              toast.success('언어 삭제가 완료되었습니다.');
              setLanguages(languages.filter((lang) => lang.id !== language.id));
              getLanguage();
            } else {
              toast.error('언어 삭제에 실패하였습니다.');
            }
          })
          .finally(() => {
            initLocales();
            broadcast({ id: 'reload', param: 'language' });
          });
      },
    });
  };

  useEffect(() => {
    getLanguage();
  }, []);

  return (
    <PageLayout
      title="언어설정"
      actionButton={
        <NewButton
          onClick={() => {
            handleLocaleChange();
          }}
        >
          언어추가
        </NewButton>
      }
    >
      <InnerSection style={{ minHeight: 'unset' }}>
        <InnerGroup>
          <div>
            <Text type="H3">제공할 언어 설정</Text>
            <Text type="H4" buttonRole="DESCRIPTION" style={{ margin: '10px 0' }}>
              컨텐츠 등록을 위한 언어 설정이 가능합니다.
              <br />
              신규 언어가 추가되는 경우, 모든 컨텐츠에 추가된 언어를 입력해야 합니다.
              <br />
              <br /> 다만, 사이트에 표시되는 정적 언어(메뉴명, 타이틀 등)는 추가로 설정을 해야
              하므로 <br />
              개발사에 문의 해 주세요.
            </Text>
          </div>

          <div>
            <Row gap={10} justifyContent="flex-start" flexWrap="wrap" marginTop={0}>
              {languages
                .filter((language) => language.isActive)
                .map((language) => (
                  <LanguageItem onClick={() => {}}>
                    <span>{language.name}</span>
                    <IconButton
                      type="delete"
                      onClick={() => handleDeleteLanguage(language)}
                      style={{ display: 'flex' }}
                    />
                  </LanguageItem>
                ))}
            </Row>
          </div>
        </InnerGroup>
      </InnerSection>

      {/* 언어 설정 관련 컨텐츠 */}
    </PageLayout>
  );
};

const LanguageItem = styled.div`
  width: 100%;
  max-width: 200px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #cccccc;
  border-radius: 10px;
  padding: 10px;
`;

export default LanguageSettingsPage;
